import React from 'react';
import { connect } from 'react-redux';
import { hideModal } from '../../actions/modal.js';
import { reloadPageifSelectedProductIsOOS } from '../../cart/actions/cart.js';
import { SPIN_THE_WHEEL } from '../../games/constants/gameRouteEntries';
import Loader from './Loader';
import { startAllSteps } from '../../actions/shared/braRecommendation.js';
import { gtmEventPush } from '../../modules/ga/GTMdatalayer.js';

import { getAssetConfig } from '../../refresh/selectors/featureFlagsSelector.ts';
import properties from '../../modules/ajio-util/properties';

  const srOnly = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0
  };

  function SRAnnounceModalName({ show, name }) {
    const lastAnnounced = React.useRef(null);
    const [msg, setMsg] = React.useState('');

    // Announce on open or when the name changes
    React.useEffect(() => {
      if (!show) return;
      if (name && name !== lastAnnounced.current) {
        setMsg(name); // triggers screen reader announcement
        lastAnnounced.current = name;
      }
    }, [show, name]);

    // Reset memory when the modal hides so re-opening re-announces
    React.useEffect(() => {
      if (!show) {
        lastAnnounced.current = null;
        setMsg(''); // clear live region text
      }
    }, [show]);
    return (
      <div
        aria-live="assertive" // or "polite" if you prefer not to interrupt
        aria-atomic="true"
        id="modalTitle"
        style={srOnly}
      >
        {msg}
      </div>
    );
  }

  const FOCUSABLE_SELECTOR = [
    'button:not([disabled]):not([tabindex="-1"])',
    '[href]:not([aria-disabled="true"])',
    // NEED TO REVISIT ABOUT TABINDEX SELECTOR, IF IT MUST BE REMOVED THEN ADD A CUSTOM DATAATTRIBUTE IN SIZEGUIDE.JS FILE AND CHECK FOR IT HERE
    'input:not([disabled]):not([tabindex="-1"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function focusFirst(container) {
    const focusables = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      el => el.offsetParent !== null
    );
    
    if (!focusables.length) return;

    const el = focusables[0];
    el.setAttribute('data-programmatic-focus', '');
    el.focus();
    el.addEventListener('blur', () => el.removeAttribute('data-programmatic-focus'), { once: true });
  }

class Modal extends React.Component {
  constructor(props) {
    super(props);
    this.firstTimeFocusHandled = false;
    this.state = {
      didUpdateCalledWithClosed : false
    }
    this.animationFrameTimeStamp = null;
    this.getTopMostDialog = this.getTopMostDialog.bind(this);
    this.handleKeyboardClicks = this.handleKeyboardClicks.bind(this)
  }
   getTopMostDialog = () => {

    // 1. Find all potential dialog nodes using semantic attributes only
    const allDialogs = Array.from(
      document.querySelectorAll('[role="dialog"][aria-modal="true"]')
    );

    if (allDialogs.length === 0) return null;

    // 2. Filter dialogs that are actually visible (display !== none)
    const visibleDialogs = allDialogs.filter(dialog => {
      const style = window.getComputedStyle(dialog);
      return style.display !== 'none';
    });

    if (visibleDialogs.length === 0) return null;

    // 3. Helper to safely extract numeric z-index
    const getZIndex = el => {
      const z = window.getComputedStyle(el).zIndex;
      if (z === 'auto') return 0;
      const parsed = parseInt(z, 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    // 4. Determine the top-most dialog
    let topDialog = visibleDialogs[0];
    let topZIndex = getZIndex(topDialog);

    for (let i = 1; i < visibleDialogs.length; i++) {
      const current = visibleDialogs[i];
      const currentZ = getZIndex(current);

      if (currentZ > topZIndex) {
        topDialog = current;
        topZIndex = currentZ;
      } else if (currentZ === topZIndex) {
        // Same z-index → later in DOM wins
        if (
          topDialog.compareDocumentPosition(current) &
          Node.DOCUMENT_POSITION_FOLLOWING
        ) {
          topDialog = current;
        }
      }
    }


    const currentActiveEl = document.activeElement;

    return topDialog
  }

  handleKeyboardClicks = (event) => {
    if (event.keyCode === 13) {
      // Check if the Enter key (key code 13) is pressed
      // event.preventDefault(); // Prevent the default form submission
    const topMostModal = this.getTopMostDialog();
    const currentModal = document.getElementById(this.props.id || 'modalId');

    if(currentModal != topMostModal){
      return
    }

    const closeBtn = document.getElementById(`closeBtn-${this.props.name}`)

    if(!topMostModal || !topMostModal.contains(document.activeElement)) return
      if (!event.currentTarget || !currentModal.contains(event.currentTarget)) {
        return;
      }
      if(document.activeElement == closeBtn){
        setTimeout(()=>{
          closeBtn.click()
        },50)

        return
      }
      document.activeElement.click();
    }
  }

  componentDidMount() {
    this.previousActiveElement = document.activeElement;
    const { id = '', show } = this.props;
    // Capture‐phase focus‐trap:
    document.addEventListener('keydown', this.handleTrapFocus, true);
    // Capture‐phase Escape handler:
    document.addEventListener('keydown', this.handleEscape, true);
    document.addEventListener('keydown', this.handleKeyboardClicks, true);
    document.addEventListener('focusin', this.handleFocusIn, true);
    this.lastFocusedInModal = null;

    // attach handler
    document.addEventListener('keydown',this.documentKeyDownHandler)
  }

  handleGAOnCancel = () => {
    const { id = '', fireSegment = () => {} } = this.props;

    if (id.includes('return_fee_post-order')) {
      gtmEventPush({
        event: 'return_fee_halfcard_interaction',
        event_category: 'return fee halfcard interaction',
        event_action: 'cross',
        page_name: 'return fee halfcard screen'
      });
    }

    if (fireSegment) fireSegment();
  };

  handleModalEvent = () => {
    const {
      show = false,
      name,
      closeButton = true,
      className = '',
      id = 'modalId',
      disableOutsideClick = false,
      redirect = false,
      fromPage = '',
      disableOnClickOfCloseButton = false,
      isFromWriteReview,
      displayContent,
      stepsReducer,
      isFromQuickView = false
    } = this.props;

    if (isFromQuickView) {
      const quickViewModal = document.getElementById('quickview-modal-dialog');

      if (quickViewModal) {
        quickViewModal.style.overflowY = 'auto';
      }
    }

    if (redirect) {
      window.history.back();
      return;
    }

    if (!disableOnClickOfCloseButton) {
      this.handleGAOnCancel();
      this.props.hideModal(name);
      if (this.props?.stepsReducer?.dressToolStepsData?.currentStep > 0) {
        this.props.startAllSteps();
      }
    }
    localStorage.setItem('ga4DressToolFireOnce', JSON.stringify("close"));
    if (this.props.name == 'sign-in-up') {
      ga('send', {
        eventCategory: 'Login',
        eventAction: 'Welcome to AJIO',
        eventLabel: 'Click on X'
      });

      if (fromPage) {
        const page = fromPage ? fromPage.toLowerCase() : '';
        switch (page) {
          case SPIN_THE_WHEEL:
            window.location = '/';
            return;
          default: // do nothing
        }
      }
    }

    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  componentDidUpdate(prevProps) {
    const { show, id, name, children } = this.props;
    const justOpened  = !prevProps.show && show;
    const justClosed  =  prevProps.show && !show;
    const contentChanged = show && prevProps.children !== children;


    const topMostModal = this.getTopMostDialog();
    const currentModal = document.getElementById(id || 'modalId');

  if (justOpened && (topMostModal == currentModal)) {
    this.previousActiveElement = document.activeElement;

    if(this.animationFrameTimeStamp){
      window.cancelAnimationFrame(this.animationFrameTimeStamp)
    }

    requestAnimationFrame(() => {
      const modalEl = document.getElementById(id || 'modalId');
      if (!modalEl) return;

      // initial focus when opening
        focusFirst(modalEl);

        // Install observer only now
        let rafId;
      const cb = (mutations) => {
        // only act on structural changes
        const structural = mutations.some(m => m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length));
        if (!structural) return;

        // debounce to next frame so DOM settles
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          if (!this.props.show) return;

          const active = document.activeElement;
          const inside = modalEl.contains(active);
          const activeHidden = inside && active instanceof HTMLElement && active.offsetParent === null;

          // if focus is still a valid, visible element inside the modal, do nothing
          if (inside && !activeHidden) return;

          // if the element we *had* in the modal still exists and is visible, restore that
          const preferred = this.lastFocusedInModal;
          if (preferred && modalEl.contains(preferred) && preferred.offsetParent !== null) {
            preferred.focus();
            return;
          }

          // otherwise, go to the first focusable
          focusFirst(modalEl);
        });
      };

      this.domObserver = new MutationObserver(cb);
      this.domObserver.observe(modalEl, { childList: true, subtree: true });
    });
  }

  // When internal step truly swaps (your prev/next children change), we still don’t steal focus
    // unless the current focus got removed/hidden. The observer above handles that.

    if (justClosed) {
      if (this.domObserver) {
        this.domObserver.disconnect();
        this.domObserver = null;
      }
      if (this.previousActiveElement) {
        this.previousActiveElement.focus();
        this.setState({
          didUpdateCalledWithClosed : true
        })

        setTimeout(()=>{
          this.setState({
            didUpdateCalledWithClosed:false
          })
        },500)
      }
    }
  }

  componentWillUnmount() {
    if (this.domObserver) {
      this.domObserver.disconnect();
      this.domObserver = null;
    }
    if (!this.state.didUpdateCalledWithClosed && this.previousActiveElement) {
      setTimeout(()=>{
        this.previousActiveElement.focus();
      },300)
    }
    document.removeEventListener('keydown', this.handleEscape, true);
    document.removeEventListener('keydown', this.handleTrapFocus, true);
    document.removeEventListener('keydown', this.handleKeyboardClicks, true);
    document.removeEventListener('focusin', this.handleFocusIn, true);

    document.removeEventListener('keydown',this.documentKeyDownHandler)
  }

  handleFocusIn = e => {
    if (!this.props.show) return;
    const modalEl = document.getElementById(this.props.id || 'modalId');
    const topMostModal = this.getTopMostDialog()
    if (!modalEl || (modalEl != topMostModal)) return;

    if (modalEl.contains(e.target)) {
      this.lastFocusedInModal = e.target;
      return;
    }

    e.stopImmediatePropagation();
    focusFirst(modalEl);
  };

  handleEscape = event => {
    if (event.key === 'Escape') {
      this.handleModalEvent();
    }
  };

  handleTrapFocus = event => {
    const { show, id } = this.props;
    if (!show || event.key !== 'Tab') return;

    const modalEl = document.getElementById(id || 'modalId');
    const topMostDialog = this.getTopMostDialog()
    if (!modalEl || (modalEl != topMostDialog)) return;

    const focusables = Array.from(modalEl.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      el => el.offsetParent !== null
    );

    if (!focusables.length) return;

    const activeEl = document.activeElement;
    if (event.key === 'Tab') {
      const active = document.activeElement;
      if (active.hasAttribute('data-programmatic-focus')) {
        active.removeAttribute('data-programmatic-focus');
        event.preventDefault();
        active.focus();
        return;
      }
    }

    // ─── FIRST TAB: “arm” keyboard focus on the same element ─────────
    if (activeEl.hasAttribute('data-programmatic-focus')) {
      // remove the programmatic-focus flag so :focus-visible fires
      activeEl.removeAttribute('data-programmatic-focus');
      // prevent the browser from moving focus this keypress
        event.preventDefault();
      // leave focus exactly where it is
      return;
    }

    // ─── SUBSEQUENT TABS: cycle normally ───────────────────────────────
    event.preventDefault();
    const idx = focusables.indexOf(activeEl);
    const nextIdx = event.shiftKey
      ? idx > 0
        ? idx - 1
        : focusables.length - 1
      : idx < focusables.length - 1
      ? idx + 1
      : 0;
    focusables[nextIdx].focus();
  };

  documentKeyDownHandler(evt) {
    evt => {
      evt = evt || window.event;

      if (evt.key == 'Enter') {
        const closeBtn = document.getElementById(`closeBtn-${name}`)
        if (closeBtn != document.activeElement) {
          return
        }
      }

      if (evt.keyCode == 27 || evt.key === "Enter") {
        this.handleGAOnCancel();
        this.props.hideModal(this.props.name);
        if (this.props?.stepsReducer?.dressToolStepsData?.currentStep > 0) {
          this.props.startAllSteps();
        }
        if (this.props.onClose) {
          this.props.onClose();
        }
      }
    };
  };

  render() {
    if (!this.props.show) return null;
    const {
      show = false,
      name,
      closeButton = true,
      className = '',
      id = 'modalId',
      disableOutsideClick = false,
      redirect = false,
      reloadPageifSelectedProductIsOOS,
      disableOnClockOfOutsideModal = false,
      isFromWriteReview,
      displayContent,
      increaseModalHeight = false,
      alignToRight = false,
      fullHeight = false,
      customClassName = '',
      assetConfig = {},
      noBackdrop = false,
    } = this.props;
    let imageBaseUrl = properties.getProperty('imageBaseUrl') || 'https://assets-jiocdn.ajio.com';
    const isJioCDNEnabled = assetConfig && assetConfig.isJioCDNEnabled === 'ON';
    if(isJioCDNEnabled) {
      imageBaseUrl = assetConfig?.jioCDNAssetUrl || '';
    }
    if (!show) {
      return null;
    }

    let closeBtnNode = null;
    if (closeButton) {
      closeBtnNode = (
        <div
          id={`closeBtn-${name}`}
          className="ic-close-quickview"
          style={{
            padding:'3px',
            margin: '-3px',
            color: '#757575'
          }}
          onClick={this.handleModalEvent}
          aria-label="close"
          role="button"
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.handleModalEvent();
            }
          }}
          tabIndex={0}
        />
      );
    }
    return (
      <div
        className={`modal fade in ${className} ${customClassName} ${noBackdrop ? 'no-backdrop' : ''}`}
        style={{ display: 'block' }}
        id={id}
        aria-modal="true"
        role="dialog"
        aria-labelledby="modalTitle"
        aria-hidden={!show}
        onClick={() => {
          if (name == 'cart-qty') {
            reloadPageifSelectedProductIsOOS();
          }
          if (!disableOutsideClick && !disableOnClockOfOutsideModal) {
            this.handleGAOnCancel();
            this.props.hideModal(name);
            if (this.props?.stepsReducer?.dressToolStepsData?.currentStep > 0) {
              this.props.startAllSteps();
            }
            if (this.props.onClose) {
              this.props.onClose();
            }
          }
        }}
      >
        {this.props.createLoader && <Loader name={this.props.loaderIdentifier} />}
        <div
          className={`modal-dialog ${increaseModalHeight ? 'modal-dialog-increased-height' : ''} ${isFromWriteReview ? 'is-write-review-card' : ''
            } ${alignToRight ? 'modal-right-aligned' : ''} ${fullHeight ? 'modal-content-full-h' : ''}`}
          id={id === 'quickview-modal' ? 'quickview-modal-dialog' : ''}
          onClick={e => {
            return e.stopPropagation();
          }}
        >
          <div
            className={`modal-content ${fullHeight ? 'modal-content-full-h' : ''}`}
            // style={{
            //   backgroundImage: `url(${`${imageBaseUrl}/static/img/catnav_mob_bg.png`})`,
            //   backgroundRepeat: 'repeat-x'
            //  }}
            onClick={e => {
              return e.stopPropagation();
            }}
          >
            <SRAnnounceModalName show={show} name={this.props.ariaLabel ?? name} />
            {closeBtnNode}
            {this.props.children}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  let otherProps = {
    fromPage: '',
    disableOnClickOfCloseButton: false,
    disableOnClockOfOutsideModal: false,
    isFromWriteReview: false,
    displayContent: '',
    increaseModalHeight: false
  };
  const { modals = {}, stepsReducer } = state;
  const { name, displayContent, increaseModalHeight } = ownProps;
  const assetConfig = getAssetConfig(state?.flags);
  let show = false;
  if (modals[name]) {
    show = true;
    const { state: modalState = {}, ariaLabel } = modals[name] || {};
    otherProps = {
      ...otherProps,
      displayContent,
      ...(modalState || {})
    };
    if (modalState.isFromWriteReview) {
      otherProps.isFromWriteReview = true;
    }
    if (ariaLabel) {
      otherProps.ariaLabel = ariaLabel;
    }
  }

  if (name === 'sign-in-up') {
    const { state: modalState = {} } = modals[name] || {};
    otherProps = {
      ...otherProps,
      ...(modalState || {}),
      assetConfig
    };
  }
  if (increaseModalHeight) {
    otherProps = {
      ...otherProps,
      increaseModalHeight: true
    };
  }
  return {
    show,
    stepsReducer,
    ...otherProps
  };
};

function mapDispatchToProps(dispatch) {
  return {
    hideModal: name => {
      dispatch(hideModal(name));
    },
    reloadPageifSelectedProductIsOOS: () => dispatch(reloadPageifSelectedProductIsOOS()),
    startAllSteps: () => dispatch(startAllSteps()),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Modal);
